import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EmailService } from "../services/email/email.service";
import { FornecedorService } from "../services/fornecedor/fornecedor.service";
import { UploadService } from "../services/upload/upload.service";
import { PropostaService } from "../services/fornecedor/proposta.service";
import { UsuarioService } from "../services/usuario/usuario.service";
import { useAuth } from "../contexts/AuthContext";
import Masks from "../utils/masks";
import Swal from "sweetalert2";
import Alert from "../components/shared/alert/Alert";

const useCadastroFornecedor = () => {
    const cadastroMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await FornecedorService.postFornecedor(payload);
            return response;
        },
        onError: async (error) => {
            const errorMessage = error.response?.data?.erro || "Erro desconhecido";
            return errorMessage;
        },
    });

    const alterarSenha = useMutation({
        mutationFn: async ({ token, payload }) => {
            const response = await UsuarioService.alterarSenha(token, payload);
            return response;
        },
        onError: async (error) => {
            const errorMessage = error.response?.data?.erro || "Erro desconhecido";
            return errorMessage;
        }
    });

    const atualizarMutation = useMutation({
        mutationFn: async ({ token, codcli, payload }) => {
            const response = await FornecedorService.updateFornecedorByCodCli(token, codcli, payload);
            return response;
        },
        onError: async (error) => {
            const errorMessage = error.response?.data?.erro || "Erro desconhecido";
            return errorMessage;
        },
    })

    const uploadMutation = useMutation({
        mutationFn: async ({ token, uploadData, binary }) => {
            return await UploadService.upload(token, uploadData, binary);
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                confirmButtonText: "OK",
                showConfirmButton: true,
                message: "Erro ao enviar os arquivos: " + message
            });
        },
    });

    return {
        cadastrarFornecedor: cadastroMutation.mutate,
        cadastrarFornecedorAsync: cadastroMutation.mutateAsync,
        atualizarFornecedor: atualizarMutation.mutate,
        atualizarFornecedorAsync: atualizarMutation.mutateAsync,
        alterarSenha: alterarSenha.mutateAsync,
        upload: uploadMutation.mutateAsync,
        isLoading:
            cadastroMutation.isPending ||
            atualizarMutation.isPending,
        isError:
            cadastroMutation.isError ||
            atualizarMutation.isError,
        isSuccess:
            cadastroMutation.isSuccess ||
            atualizarMutation.isSuccess,
        error: cadastroMutation.error,
        data: cadastroMutation.data,
    };
};

const useQuestionario = () => {
    const { user } = useAuth();
    const { upload } = useCadastroFornecedor();

    const cadastroFinalMutation = useMutation({
        mutationFn: async (data) => {
            return await FornecedorService.postCadastroFinal(user.token, data);
        },
        onError: async (error) => {
            const errorMessage = error || error.message;
            return errorMessage;
        },
    });

    const filtrarFornecedorMutation = useMutation({
        mutationFn: async (codope) => {
            const response = await FornecedorService.filtrarFornecedores(user.token, {
                filtros: [{
                    "campo": "cgccli",
                    "valor": codope,
                    "uselike": true
                }]
            });
            return response.docs[0]?.codcli;
        },
    });

    const enviarQuestionario = async (formData, files) => {
        try {
            const data = {
                alvara: formData.reqLegal_0 || "NA",
                licenc: formData.reqLegal_1 || "NA",
                frnloc: formData.reqLegal_2 || "NA",
                frnsus: formData.reqLegal_3 || "NA",
                imppos: formData.reqLegal_4 || "NA",
                prlgpd: formData.reqLegal_5 || "NA",

                sisqua: formData.qualidade_0 || "0",
                anasol: formData.qualidade_1 || "0",
                inspro: formData.qualidade_2 || "0",
                regras: formData.qualidade_3 || "0",
                cercal: formData.qualidade_4 || "0",
                insdur: formData.qualidade_5 || "0",
                contro: formData.qualidade_6 || "0",
                cerqua: formData.qualidade_7 || "0",
                lidpro: formData.qualidade_8 || "0",

                nmcli1: formData.cliente1Razao || "",
                fncli1: Masks.removeMask(formData.cliente1Telefone) || "",
                nmcli2: formData.cliente2Razao || "",
                fncli2: Masks.removeMask(formData.cliente2Telefone) || "",
                nmcli3: formData.cliente3Razao || "",
                fncli3: Masks.removeMask(formData.cliente3Telefone) || "",
            };

            let codope = user.codope;

            if (isNaN(codope)) {
                codope = await filtrarFornecedorMutation.mutateAsync(codope);
            }

            const response = await cadastroFinalMutation.mutateAsync(data);

            if (response.docs.arq486 > 0 && files.length > 0) {
                const uploadPromises = files.map(({ binary, idx }) =>
                    upload({
                        token: user.token,
                        uploadData: {
                            nomTab: "ARQ005",
                            seqTab1: codope,
                            seqTab2: idx + 1,
                            seqTab3: response.docs.arq486,
                            pasta: "Aprovacoes\\" + response.docs.codcli + "\\" + response.docs.arq486
                        },
                        binary
                    })
                );

                await Promise.all(uploadPromises);
            }

            return response;
        } catch (error) {
            const errorMessage = error.response?.data?.erro || "Erro desconhecido";
            return errorMessage;
        }
    };

    return {
        enviarQuestionario,
        isLoading:
            cadastroFinalMutation.isPending ||
            filtrarFornecedorMutation.isPending,
        isError:
            cadastroFinalMutation.isError ||
            filtrarFornecedorMutation.isError,
    };
};

const usePendencias = (user) => {
    const [pageAvaliacao, setPageAvaliacao] = useState(1);
    const [pageQuestionario, setPageQuestionario] = useState(1);
    const [pageVencimento, setPageVencimento] = useState(1);
    const [pagePropostasPendentesComResposta, setPagePropostasPendentesComResposta] = useState(1);
    const [pagePropostasPendentesSemResposta, setPagePropostasPendentesSemResposta] = useState(1);

    const limit = 4;
    const hoje = new Date();
    const dataFim = hoje.toISOString().split("T")[0];
    let dataIni = new Date(hoje);
    dataIni.setFullYear(dataIni.getFullYear() - 1)
    dataIni = dataIni.toISOString().split("T")[0];

    localStorage.setItem('proposalTableFilters_dataInicio', dataIni);
    localStorage.setItem('proposalTableFilters_dataFim', dataFim);

    const fornecedorSemAvaliacaoQuery = useQuery({
        queryKey: ["fornecedorSemAvaliacao", user.token, pageAvaliacao, limit],
        queryFn: async () => {
            const response = await FornecedorService.getFornecedoresSemAvaliacao(
                user.token,
                true,
                pageAvaliacao,
                limit
            );
            return response || { docs: [], total: 0, totalPages: 1 };
        },
        enabled: !!user.token && user.tipusu === "COMPRADOR",
        staleTime: 15000,
    });

    const fornecedorSemQuestionarioQuery = useQuery({
        queryKey: ["fornecedorSemQuestionario", user.token, pageQuestionario, limit],
        queryFn: async () => {
            const response = await FornecedorService.getFornecedoresSemQuestionario(
                user.token,
                true,
                pageQuestionario,
                limit
            );
            return response || { docs: [], total: 0, totalPages: 1 };
        },
        enabled: !!user.token && user.tipusu === "COMPRADOR",
        staleTime: 15000,
    });

    const fornecedorProximoAoVencimentoQuery = useQuery({
        queryKey: ["fornecedorProximoAoVencimento", user.token, pageVencimento, limit],
        queryFn: async () => {
            const response = await FornecedorService.getFornecedoresProximosAoVencimento(
                user.token,
                true,
                pageVencimento,
                limit
            );
            return response || { docs: [], total: 0, totalPages: 1 };
        },
        enabled: !!user.token && user.tipusu === "COMPRADOR",
        staleTime: 15000,
    });

    const propostasPendentesSemResposta = useQuery({
        queryKey: ["propostasPendentesSemResposta", user.token, dataIni, dataFim, pagePropostasPendentesSemResposta, limit],
        queryFn: async () => {
            const response = await PropostaService.getPropostasPendentesAgrupado(
                user.token,
                0,
                dataIni,
                dataFim,
                true,
                pagePropostasPendentesSemResposta,
                limit
            );

            return response || { docs: [], total: 0, totalPages: 1 };
        },
        enabled: !!user.token,
        staleTime: 15000,
    });

    const propostasPendentesComResposta = useQuery({
        queryKey: ["propostasPendentesComResposta", user.token, dataIni, dataFim, pagePropostasPendentesComResposta, limit],
        queryFn: async () => {
            const response = await PropostaService.getPropostasPendentesAgrupado(
                user.token,
                1,
                dataIni,
                dataFim,
                true,
                pagePropostasPendentesComResposta,
                limit
            );

            return response || { docs: [], total: 0, totalPages: 1 };
        },
        enabled: !!user.token,
        staleTime: 15000,
    });

    return {
        fornecedoresSemAvaliacao: fornecedorSemAvaliacaoQuery.data,
        fornecedoresSemQuestionario: fornecedorSemQuestionarioQuery.data,
        fornecedoresProximosAoVencimento: fornecedorProximoAoVencimentoQuery.data,
        propostasPendentesComResposta: propostasPendentesComResposta.data,
        propostasPendentesSemResposta: propostasPendentesSemResposta.data,
        isLoading:
            (fornecedorSemAvaliacaoQuery.isEnabled && fornecedorSemAvaliacaoQuery.isPending) ||
            (fornecedorSemQuestionarioQuery.isEnabled && fornecedorSemQuestionarioQuery.isPending) ||
            (fornecedorProximoAoVencimentoQuery.isEnabled && fornecedorProximoAoVencimentoQuery.isPending) ||
            (propostasPendentesComResposta.isEnabled && propostasPendentesComResposta.isPending) ||
            (propostasPendentesSemResposta.isEnabled && propostasPendentesSemResposta.isPending),
        isError:
            (fornecedorSemAvaliacaoQuery.isEnabled && fornecedorSemAvaliacaoQuery.isError) ||
            (fornecedorSemQuestionarioQuery.isEnabled && fornecedorSemQuestionarioQuery.isError) ||
            (fornecedorProximoAoVencimentoQuery.isEnabled && fornecedorProximoAoVencimentoQuery.isError),
        pageAvaliacao,
        setPageAvaliacao,
        totalPagesAvaliacao:
            fornecedorSemAvaliacaoQuery.data?.pages ?? 1,
        pageQuestionario,
        setPageQuestionario,
        totalPagesQuestionario:
            fornecedorSemQuestionarioQuery.data?.pages ?? 1,
        pageVencimento,
        setPageVencimento,
        totalPagesVencimento:
            fornecedorProximoAoVencimentoQuery.data?.pages ?? 1,
        pagePropostasPendentesSemResposta,
        setPagePropostasPendentesSemResposta,
        totalPagesPropostasPendentesSemResposta:
            propostasPendentesSemResposta.data?.pages ?? 1,
        pagePropostasPendentesComResposta,
        setPagePropostasPendentesComResposta,
        totalPagesPropostasPendentesComResposta:
            propostasPendentesComResposta.data?.pages ?? 1,
        refetchAll: () => {
            fornecedorSemAvaliacaoQuery.refetch();
            fornecedorProximoAoVencimentoQuery.refetch();
            fornecedorSemQuestionarioQuery.refetch();
            propostasPendentesComResposta.refetch();
            propostasPendentesSemResposta.refetch();
        },
    };
};

const useFornecedores = (user, codcli, enabled = true) => {
    const queryClient = useQueryClient();
    const { upload } = useCadastroFornecedor();

    const consultaSintegraQuery = useQuery({
        queryKey: ["sintegra", codcli],
        queryFn: async () => {
            const data = await FornecedorService.consultaSintegra(user?.token, codcli);
            return data;
        },
        retry: false,
        enabled: !!user?.token && !!codcli && enabled,
        onError: (error) => {
            const message = error.response?.data?.erro || error.message;
            Alert({
                icon: "error",
                title: "Erro",
                message: `${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const fornecedorQuery = useQuery({
        queryKey: ["fornecedor", codcli],
        queryFn: async () => {
            const data = await FornecedorService.getFornecedorByCodCli(user?.token, codcli);
            return data;
        },
        retry: false,
        enabled: !!user?.token && !!codcli && enabled,
        onError: (error) => {
            const message = error.response?.data?.erro || error.message;
            Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao carregar fornecedor: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const avaliacoesQuery = useQuery({
        queryKey: ["avaliacoes", codcli],
        queryFn: async () => {
            try {
                const response = await FornecedorService.getAvaliacoesByCodCli(user?.token, codcli);
                return Array.isArray(response) ? response : [];
            } catch (error) {
                console.warn("Erro ao carregar avaliações:", error);
                return [];
            }
        },
        enabled: !!user?.token && !!codcli && enabled,
    });

    const gerarSenhaTemporariaMutation = useMutation({
        mutationFn: async () => {
            if (user.tipusu === "COMPRADOR") await FornecedorService.gerarSenhaTemporaria(user.token, codcli);
        },
        onSuccess: () => {
            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Senha temporária gerada com sucesso!", 
                showConfirmButton: true, 
                confirmButtonText: "OK"
            }) 
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao gerar a senha temporária para o fornecedor: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    })

    const ativarMutation = useMutation({
        mutationFn: async () => {
            if (user.tipusu === "COMPRADOR") await FornecedorService.ativarFornecedor(user.token, codcli)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["fornecedor", codcli] });
            queryClient.invalidateQueries({ queryKey: ["fornecedores"] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Fornecedor ativado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao ativar fornecedor: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const inativarMutation = useMutation({
        mutationFn: async () => {
            if (user.tipusu === "COMPRADOR") await FornecedorService.inativarFornecedor(user.token, codcli)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["fornecedor", codcli] });
            queryClient.invalidateQueries({ queryKey: ["fornecedores"] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Fornecedor inativado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao inativar fornecedor: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const novaAvaliacaoMutation = useMutation({
        mutationFn: async (codope) => {
            if (user.tipusu === "COMPRADOR") return await FornecedorService.postAvaliacaoCadastro(user.token, codcli, { "codope": codope });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["avaliacoes", codcli] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Registro de avaliação criado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao criar avaliação: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const aprovarCadInicialMutation = useMutation({
        mutationFn: async ({ avaliacaoId, observacao, arquivos }) => {
            if (user.tipusu === "COMPRADOR") {
                const response = await FornecedorService.aprovarCadInicialFornecedor(
                    user.token,
                    user.codope,
                    avaliacaoId,
                    fornecedorQuery.data?.endele,
                    codcli,
                    observacao
                )

                if (response.docs.codcli > 0 && arquivos.length > 0) {
                    const uploadPromises = arquivos.map(({ binary, idx }) =>
                        upload({
                            token: user.token,
                            uploadData: {
                                nomTab: "ARQ005",
                                seqTab1: response.docs.codcli,
                                seqTab2: 7,
                                seqTab3: avaliacaoId,
                                pasta: "Aprovacoes\\" + response.docs.codcli + "\\" + avaliacaoId
                            },
                            binary
                        })
                    );

                    await Promise.all(uploadPromises);
                }
            }
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["avaliacoes", codcli] }),
                queryClient.invalidateQueries({ queryKey: ["documentos", codcli] })
            ]);

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Cadastro inicial aprovado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao aprovar cadastro inicial: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const reprovarCadInicialMutation = useMutation({
        mutationFn: async ({ avaliacaoId, observacao }) => {
            if (user.tipusu === "COMPRADOR") {
                await FornecedorService.reprovarCadInicialFornecedor(
                    user.token,
                    user.codope,
                    avaliacaoId,
                    fornecedorQuery.data?.endele,
                    codcli,
                    observacao
                )
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["avaliacoes", codcli] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Cadastro inicial reprovado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao reprovar cadastro inicial: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const aprovarCadFinalMutation = useMutation({
        mutationFn: async ({ avaliacaoId, observacao }) => {
            if (user.tipusu === "COMPRADOR") {
                await FornecedorService.aprovarCadFinalFornecedor(
                    user.token,
                    user.codope,
                    avaliacaoId,
                    fornecedorQuery.data?.endele,
                    codcli,
                    observacao
                )
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["avaliacoes", codcli] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Cadastro final aprovado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao aprovar cadastro final: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const reprovarCadFinalMutation = useMutation({
        mutationFn: async ({ avaliacaoId, observacao }) => {
            if (user.tipusu === "COMPRADOR") {
                await FornecedorService.reprovarCadFinalFornecedor(
                    user.token,
                    user.codope,
                    avaliacaoId,
                    fornecedorQuery.data?.endele,
                    codcli,
                    observacao
                )
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["avaliacoes", codcli] });

            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Cadastro final reprovado com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao reprovar cadastro final: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const enviarEmailMutation = useMutation({
        mutationFn: async ({ destinatario, assunto, conteudo }) => {
            const data = {
                "destinatario": destinatario,
                "assunto": assunto,
                "conteudo": conteudo
            };
            await EmailService.enviarEmail(data)
        },
        onSuccess: () => {
            Alert({
                icon: "success",
                title: "Sucesso",
                message: "Email enviado para o fornecedor com sucesso!",
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
        onError: async (error) => {
            const message = error.response?.data?.erro || error.message;
            await Alert({
                icon: "error",
                title: "Erro",
                message: `Erro ao enviar email: ${message}`,
                showConfirmButton: true,
                confirmButtonText: "OK",
            });
        },
    });

    const handleGerarSenhaTemporaria = useCallback(async () => {
       const confirmed = await Alert({
            title: "Gerar senha temporária",
            message: "Tem certeza que deseja gerar uma senha temporária com o CPF/CNPJ do fornecedor?",
            icon: "warning",
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            showCancelButton: true,
            cancelButtonText: "Cancelar"
        });

        if (confirmed) {
            gerarSenhaTemporariaMutation.mutate();
        }
    }, [gerarSenhaTemporariaMutation]);

    const handleAtivar = useCallback(async () => {
        const confirmed = await Alert({
            title: "Ativar fornecedor",
            message: "Tem certeza que deseja ativar o fornecedor?",
            icon: "warning",
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            showCancelButton: true,
            cancelButtonText: "Cancelar"
        });

        if (confirmed) {
            ativarMutation.mutate();
        }
    }, [ativarMutation]);

    const handleInativar = useCallback(async () => {
        const confirmed = await Alert({
            title: "Inativar fornecedor",
            message: "Tem certeza que deseja inativar o fornecedor?",
            icon: "warning",
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            showCancelButton: true,
            cancelButtonText: "Cancelar"
        });

        if (confirmed) {
            inativarMutation.mutate();
        }
    }, [inativarMutation]);

    const handleNovaAvaliacao = useCallback(async () => {
        let message = '';

        if (avaliacoesQuery.data.length > 0) {
            message = 'Confirma a inclusão de um novo Registro de Avaliação para esse fornecedor? A última avaliação foi realizada em ' +
                avaliacoesQuery.data[0].datcad
        } else {
            message = 'Confirma a inclusão do primeiro Registro de Avaliação para esse fornecedor?';
        }


        const confirmed = await Alert({
            title: "Nova Avaliação",
            message,
            icon: "info",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
        });

        if (confirmed) {
            novaAvaliacaoMutation.mutate(user.codope);
        }
    }, [novaAvaliacaoMutation, user?.codope]);

    const handleAprovarAvaliacao = useCallback(async (avaliacaoId, tipoCadastro) => {
        const titulo = tipoCadastro === "CI" ? "Aprovação Fiscal" : "Aprovação Final";
        const mensagem = `Confirma a aprovação desta avaliação ${tipoCadastro === "CI" ? "fiscal" : "final"}?`;

        if (tipoCadastro === "CI") {
            const result = await Alert({
                title: titulo,
                message: mensagem,
                icon: "success",
                input: "textarea",
                inputPlaceholder: "Digite uma observação...",
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
                didOpen: () => {
                    const container = Swal.getHtmlContainer();

                    container.style.display = "flex";
                    container.style.flexDirection = "column";
                    container.style.gap = "12px";
                    container.style.alignItems = "stretch";

                    const textarea = Swal.getInput();
                    textarea.style.minHeight = "100px";
                    textarea.style.resize = "vertical";

                    const fileInput = document.createElement("input");
                    fileInput.type = "file";
                    fileInput.accept = "application/pdf"
                    fileInput.id = "swal-arquivo";
                    fileInput.multiple = false;
                    fileInput.classList.add("swal2-file");

                    fileInput.style.width = "100%";
                    fileInput.style.padding = "8px";
                    fileInput.style.border = "1px solid #ccc";
                    fileInput.style.borderRadius = "4px";
                    fileInput.style.background = "#fff";

                    container.appendChild(fileInput);
                },

                preConfirm: () => {
                    const arquivos = document.getElementById("swal-arquivo").files[0];
                    const observacao = Swal.getInput().value;

                    return { observacao, arquivos };
                }
            });

            if (!result?.observacao || result.observacao?.trim() === '') {
                if (result !== null) {
                    await Alert({
                        icon: "warning",
                        title: "Atenção",
                        message: "É necessário preencher a observação.",
                        showConfirmButton: true,
                        confirmButtonText: "OK",
                    });
                }
                return;
            }

            let arquivo = null;

            if (result?.arquivos) {
                const file = result.arquivos;

                if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
                    await Alert({
                        icon: "error",
                        title: "Arquivo inválido",
                        message: "Envie apenas um arquivo PDF.",
                        confirmButtonText: "OK",
                        showConfirmButton: true,
                    });

                    return;
                }

                arquivo = { binary: file, idx: 0 };
            }

            const params = {
                avaliacaoId,
                observacao: result.observacao,
                arquivos: arquivo ? [arquivo] : []
            };

            aprovarCadInicialMutation.mutate(params);
        } else if (tipoCadastro === "CF") {
            const result = await Alert({
                title: titulo,
                message: mensagem,
                icon: "success",
                input: "textarea",
                inputPlaceholder: "Digite uma observação...",
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
            });

            if (result === null || !result.trim()) {
                if (result !== null) {
                    await Alert({
                        icon: "warning",
                        title: "Atenção",
                        message: "É necessário preencher a observação.",
                        showConfirmButton: true,
                        confirmButtonText: "OK",
                    });
                }
                return;
            }
            const params = { avaliacaoId, observacao: result };
            aprovarCadFinalMutation.mutate(params);
        }
    }, [aprovarCadInicialMutation, aprovarCadFinalMutation]);

    const handleReprovarAvaliacao = useCallback(async (avaliacaoId, tipoCadastro) => {
        const titulo = tipoCadastro === "CI" ? "Reprovação Fiscal" : "Reprovação Final";
        const mensagem = `Confirma a reprovação desta avaliação ${tipoCadastro === "CI" ? "fiscal" : "final"}?`;

        const result = await Alert({
            title: titulo,
            message: mensagem,
            icon: "error",
            input: "textarea",
            inputPlaceholder: "Digite uma observação...",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
        });

        if (result === null || !result.trim()) {
            if (result !== null) {
                await Alert({
                    icon: "warning",
                    title: "Atenção",
                    message: "É necessário preencher a observação.",
                    showConfirmButton: true,
                    confirmButtonText: "OK",
                });
            }
            return;
        }

        const params = { avaliacaoId, observacao: result };

        if (tipoCadastro === "CI") {
            reprovarCadInicialMutation.mutate(params);
        } else {
            reprovarCadFinalMutation.mutate(params);
        }
    }, [reprovarCadInicialMutation, reprovarCadFinalMutation]);

    const handleEnviarEmail = useCallback(async (destinatario, assunto, conteudo) => {
        enviarEmailMutation.mutate({ destinatario, assunto, conteudo });
    }, [enviarEmailMutation]);

    const isLoading =
        fornecedorQuery.isLoading ||
        avaliacoesQuery.isLoading ||
        ativarMutation.isPending ||
        inativarMutation.isPending ||
        gerarSenhaTemporariaMutation.isPending || 
        novaAvaliacaoMutation.isPending ||
        aprovarCadInicialMutation.isPending ||
        reprovarCadInicialMutation.isPending ||
        aprovarCadFinalMutation.isPending ||
        reprovarCadFinalMutation.isPending ||
        enviarEmailMutation.isPending || 
        consultaSintegraQuery.isPending ;

    return {
        fornecedor: fornecedorQuery.data,
        avaliacoes: avaliacoesQuery.data,
        consultaSintegra: consultaSintegraQuery.data,

        isLoading,
        isLoadingFornecedor: fornecedorQuery.isLoading,
        isLoadingAvaliacoes: avaliacoesQuery.isLoading,
        isLoadingAction:
            ativarMutation.isPending ||
            inativarMutation.isPending ||
            novaAvaliacaoMutation.isPending ||
            aprovarCadInicialMutation.isPending ||
            reprovarCadInicialMutation.isPending ||
            aprovarCadFinalMutation.isPending ||
            reprovarCadFinalMutation.isPending ||
            enviarEmailMutation.isPending ||
            gerarSenhaTemporariaMutation.isPending,

        errorFornecedor: fornecedorQuery.error,
        errorAvaliacoes: avaliacoesQuery.error,

        ativarFornecedor: handleAtivar,
        inativarFornecedor: handleInativar,
        gerarSenhaTemporaria: handleGerarSenhaTemporaria,
        novaAvaliacao: handleNovaAvaliacao,
        aprovarAvaliacao: handleAprovarAvaliacao,
        reprovarAvaliacao: handleReprovarAvaliacao,
        enviarEmail: handleEnviarEmail,

        refetchFornecedor: fornecedorQuery.refetch,
        refetchAvaliacoes: avaliacoesQuery.refetch,
    };
};

const useDocumentos = (codcli, registroId, shouldLoad = false) => {
    const [documentos, setDocumentos] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const CATEGORIAS_ARQUIVO = {
        1: "Alvará de Funcionamento",
        2: "Licenças Ambientais",
        3: "Fornecedor Local (GRI 204-1)",
        4: "Fornecedor Sustentável (GRI 308-1)",
        5: "Impacto Social (GRI 414-1)",
        6: "Práticas LGPD",
        7: "Análise Fiscal e Financeira"
    };

    const documentosCache = useRef(new Map());

    const limparURLCache = useCallback(() => {
        documentosCache.current.forEach((docs) => {
            docs.forEach((doc) => {
                if (doc.url && doc.url.startsWith("blob:")) {
                    URL.revokeObjectURL(doc.url);
                }
            });
        });
        documentosCache.current.clear();

        queryClient.removeQueries({ queryKey: ["documentos", codcli, registroId] });
    }, [codcli, registroId, queryClient]);

    const fetchDocumentos = useCallback(async () => {
        if (!user?.token || !codcli || !registroId) return [];

        const cacheKey = `${codcli}_${registroId}`;
        if (documentosCache.current.has(cacheKey)) {
            return documentosCache.current.get(cacheKey);
        }

        const limit = user.tipusu === "COMPRADOR" ? 7 : 6;

        const promises = [];
        for (let categoriaId = 1; categoriaId <= limit; categoriaId++) {
            promises.push(
                UploadService.download(user.token, {
                    codope: user.codope,
                    nomTab: "ARQ005",
                    seqTab1: codcli,
                    seqTab2: categoriaId,
                    seqTab3: registroId,
                })
                    .then((response) => {
                        if (response && response.size > 0) {
                            const blob = new Blob([response], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);

                            return {
                                id: `${registroId}_${categoriaId}`,
                                nome: `${CATEGORIAS_ARQUIVO[categoriaId]}.pdf`,
                                url,
                                categoriaId,
                                registroId,
                            };
                        }
                        return null;
                    })
                    .catch(() => null)
            );
        }

        const results = await Promise.all(promises);
        const allDocs = results.filter(Boolean);

        documentosCache.current.set(cacheKey, allDocs);
        return allDocs;
    }, [user?.token, codcli, registroId]);

    const {
        data,
        isFetching,
        isError,
        error,
        refetch
    } = useQuery({
        queryKey: ["documentos", codcli, registroId],
        queryFn: fetchDocumentos,
        enabled: shouldLoad && !!registroId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
    });

    const carregarDocumentos = useCallback(async () => {
        if (!registroId || carregando) return;
        setCarregando(true);
        setErro(null);

        try {
            const docs = await refetch();
            setDocumentos(docs.data || []);
        } catch (err) {
            console.error("Ocorreu um erro ao carregar os documentos:", err);
            setErro("Ocorreu um erro ao carregar os documentos.");
            setDocumentos([]);
        } finally {
            setCarregando(false);
        }
    }, [registroId, carregando, refetch]);

    const recarregarDocumentos = useCallback(async () => {
        if (!registroId) return;

        const cacheKey = `${codcli}_${registroId}`;
        documentosCache.current.delete(cacheKey);
        queryClient.invalidateQueries(["documentos", codcli, registroId]);

        await carregarDocumentos();
    }, [registroId, codcli, carregarDocumentos, queryClient]);

    useEffect(() => {
        if (shouldLoad && registroId && data) {
            setDocumentos(data);
        }
    }, [shouldLoad, registroId, data]);

    useEffect(() => {
        if (isError) {
            setErro(error?.message || "Erro ao carregar documentos.");
        }
    }, [isError, error]);

    return {
        documentos,
        carregando: carregando || isFetching,
        erro,
        carregarDocumentos,
        recarregarDocumentos,
        limparURLCache
    };
};

export { useFornecedores, useCadastroFornecedor, useQuestionario, useDocumentos, usePendencias };